import axios from 'axios';
import i18n from '../i18n';
// import api from '../../services/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Coduri de 401 care forțează delogarea (cont invalid / sesiune revocată sau expirată).
const LOGOUT_CODES = [
  'USER_DELETED',
  'USER_INACTIVE',
  'TOKEN_EXPIRED',
  'TOKEN_INVALID',
  'TOKEN_REVOKED',
  'NO_TOKEN',
];

// ✅ Interceptor response — traduce erorile din backend + deloghează dacă contul e șters/inactiv
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const data = error.response?.data;

    // Traducere centralizată: dacă răspunsul poartă un `code` stabil cu o cheie i18n
    // (errorCodes.<COD>), rescriem `error` în limba curentă. Toate call-site-urile care citesc
    // err.response.data.error primesc textul tradus, fără modificări punctuale. Codurile nemapate
    // (ex: VALIDATION_ERROR, unde păstrăm mesajul specific de câmp) lasă textul neatins.
    if (data?.code && i18n.exists(`errorCodes.${data.code}`)) {
      data.error = i18n.t(`errorCodes.${data.code}`, data.params || {});
    }

    if (error.response?.status === 401 && LOGOUT_CODES.includes(data?.code)) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Redirect la login dacă nu suntem deja acolo
      if (!window.location.pathname.includes('/login')) {
        // Mesajul (deja tradus mai sus în `data.error`) se afișează pe pagina de login.
        sessionStorage.setItem('logoutMessage', data.error);
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Media protejat (video-uri module, materiale PDF/PPTX, video intro curs).
// Fișierele NU mai sunt expuse static la /uploads — se obține un URL semnat scurt prin /media/sign
// (autorizat pe resursă), apoi <video>/react-pdf consumă /media/stream?token=... (suport Range).
export const mediaAPI = {
  sign: (path) => api.get('/media/sign', { params: { path } }),
};

// Întoarce un URL absolut, autorizat, pentru un fișier media. Pentru URL-uri externe (YouTube/Vimeo)
// întoarce valoarea ca atare. Pentru căi locale „/uploads/..." cere un token semnat și construiește
// URL-ul de streaming. Întoarce null dacă lipsește calea sau dacă userul nu are acces.
export const getSignedMediaUrl = async (relPath) => {
  if (!relPath) return null;
  if (/^https?:\/\//i.test(relPath)) return relPath;
  try {
    const { data } = await mediaAPI.sign(relPath);
    const base = API_BASE_URL.replace(/\/api\/?$/, '');
    return `${base}${data.url}`;
  } catch (_) {
    return null;
  }
};

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/users/me'),
  impersonate: (userId) => api.post(`/auth/impersonate/${userId}`),
  stopImpersonation: () => api.post('/auth/stop-impersonation'),
};

export const employeesAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`), // HARD delete (definitiv)
  deactivate: (id) => api.patch(`/employees/${id}/deactivate`), // soft — reversibil
  activate: (id) => api.patch(`/employees/${id}/activate`),
  getProfile: () => api.get('/employees/me'),
  bulkUpdateIPIntervals: (payload) => api.patch('/employees/bulk/ip-intervals', payload),
  getAccessPassword: (id) => api.get(`/employees/${id}/access-password`),
  regenerateAccessPassword: (id, payload) =>
    api.post(`/employees/${id}/access-password/regenerate`, payload),
};

export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
  addUser: (id, data) => api.post(`/companies/${id}/users`, data),
  addAdmin: (companyId, data) => api.post(`/companies/${companyId}/admins`, data),
  getServiceImpact: (id, service) => api.get(`/companies/${id}/service-impact?service=${service}`),
  uploadLogo: (id, variant, file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.post(`/companies/${id}/logo?variant=${variant}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: (id, variant) => api.delete(`/companies/${id}/logo?variant=${variant}`),
  updateBranding: (id, data) => api.patch(`/companies/${id}/branding`, data),
  toggleWhiteLabel: (id, enabled) => api.patch(`/companies/${id}/white-label`, { enabled }),
};

// Courses - EXTENDED
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  getMyCourses: () => api.get('/courses/my/assigned'),
  create: (data) => api.post('/courses', data),
  clone: (id, data) => api.post(`/courses/${id}/clone`, data || {}),
  listTemplates: (params) => api.get('/courses/templates', { params }),
  getTemplate: (id) => api.get(`/courses/templates/${id}`),
  toggleTemplate: (id, isTemplate) => api.patch(`/courses/${id}/template-status`, { isTemplate }),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  addModule: (courseId, data) => api.post(`/courses/${courseId}/modules`, data),
  assign: (courseId, data) => api.post(`/courses/${courseId}/assign`, data),
  getAssignments: (courseId) => api.get(`/courses/${courseId}/assignments`),
  getStats: (id) => api.get(`/courses/${id}/stats`),
};

// Atribuiri (bulk) — preview înainte de trimitere + anulare a celor neîncepute
export const assignmentsAPI = {
  preview: (data) => api.post('/assignments/preview', data),
  bulk: (data) => api.post('/assignments/bulk', data),
  revoke: (id) => api.delete(`/assignments/${id}`),
  adminCandidates: (companyId) =>
    api.get('/assignments/admin-candidates', { params: { companyId } }),
};

export const departmentsAPI = {
  getAll: (companyId) => api.get('/departments', { params: { companyId } }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
};

export const importAPI = {
  uploadEmployees: (formData) =>
    api.post('/import/employees', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  downloadTemplate: () => api.get('/import/template', { responseType: 'blob' }),
};

export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  toggleReminders: (enabled) => api.put('/notifications/toggle-reminders', { enabled }),
  checkIPReminders: () => api.post('/notifications/check-ip-reminders'),
};

export const dashboardAPI = {
  getIPSemaforizare: () => api.get('/dashboard/ip-semaforizare'),
  getExecutive: (companyId) =>
    api.get('/dashboard/executive', companyId ? { params: { companyId } } : undefined),
  getPortfolio: () => api.get('/dashboard/portfolio'),
  getMyCompliance: () => api.get('/dashboard/my-compliance'),
};

// Instruiri (parcurgere) — folosit de widget-urile de progres + autoinstruirea 1-click.
export const trainingsAPI = {
  selfEnroll: (courseId) => api.post('/trainings/self-enroll', { courseId }),
};

// Raport ITM — date agregate (JSON) + exporturi (PDF / Excel).
// Permisiuni fine verificate în backend pe baza tenant-ului firmei.
export const itmReportAPI = {
  getData: (companyId) => api.get(`/companies/${companyId}/itm-report/data`),
  downloadPDF: (companyId) =>
    api.get(`/companies/${companyId}/itm-report`, { responseType: 'blob' }),
  downloadExcel: (companyId) =>
    api.get(`/companies/${companyId}/itm-report/excel`, { responseType: 'blob' }),
};

export const delegationsAPI = {
  list: (companyId) => api.get(`/companies/${companyId}/delegations`),
  create: (companyId, data) => api.post(`/companies/${companyId}/delegations`, data),
  revoke: (companyId, delegationId) =>
    api.delete(`/companies/${companyId}/delegations/${delegationId}`),
  transferResponsibility: (companyId, newResponsibleId) =>
    api.patch(`/companies/${companyId}/signature-responsible`, { newResponsibleId }),
};

// CLM — Conducători Loc de Muncă (multi-CLM: permanenți sau temporari, pe departament / pe toată firma).
// Administrat de firma SSM. Sursa de adevăr pentru cine semnează slot-ul de conducător pe fișe.
export const clmAPI = {
  getCompanyClm: (companyId) => api.get(`/clm/${companyId}`),
  create: (companyId, data) => api.post(`/clm/${companyId}`, data),
  update: (companyId, mandateId, data) => api.patch(`/clm/${companyId}/${mandateId}`, data),
  remove: (companyId, mandateId) => api.delete(`/clm/${companyId}/${mandateId}`),
  // Împuterniciri de admin (director/CLM/manager/secretară/HR → puteri de COMPANY_ADMIN).
  createAdmin: (companyId, data) => api.post(`/clm/${companyId}/admin`, data),
  updateAdmin: (companyId, mandateId, data) =>
    api.patch(`/clm/${companyId}/admin/${mandateId}`, data),
  removeAdmin: (companyId, mandateId) => api.delete(`/clm/${companyId}/admin/${mandateId}`),
};

export const emailPreferencesAPI = {
  get: () => api.get('/email-preferences'),
  update: (data) => api.put('/email-preferences', data),
};

// Identitatea de semnătură electronică avansată (AdES, eIDAS Art. 26).
// Cheia se generează o singură dată, după ce userul are parolă schimbată + CNP setat.
export const signingAPI = {
  status: () => api.get('/signing/status'),
  generate: (password) => api.post('/signing/generate', { password }),
};

// Fișa postului — anexă bilingvă RO/EN la fișa SSM/SU, semnată de angajat + counterpart.
// Materializare lazy în getMyFlow; flow-ul declanșat după ce ILM_SSM/SU (sau IIG pentru admin) e completat.
export const jobDescriptionAPI = {
  getMyTemplates: () => api.get('/job-descriptions/templates'),
  upsertTemplate: (domain, data) => api.put(`/job-descriptions/templates/${domain}`, data),
  getMyFlow: () => api.get('/job-descriptions/my-flow'),
  getPendingForMe: () => api.get('/job-descriptions/pending-for-me'),
  sign: (instanceId, password) => api.post(`/job-descriptions/${instanceId}/sign`, { password }),
  bulkSign: (instanceIds, password) =>
    api.post('/job-descriptions/bulk-sign', { instanceIds, password }),
  getPdfUrl: (instanceId) => `${API_BASE_URL}/job-descriptions/${instanceId}/pdf`,
  getFullSheetUrl: (employeeId) =>
    `${API_BASE_URL}/job-descriptions/employee/${employeeId}/full-sheet`,
};

// GDPR — drepturile persoanei vizate (export date personale + ștergere/anonimizare cont propriu).
export const gdprAPI = {
  exportMyData: () => api.get('/users/me/export', { responseType: 'blob' }),
  deleteMyAccount: (password) => api.delete('/users/me', { data: { password } }),
};

export default api;
